import type { ReactNode } from "react";
import { X } from "lucide-react";

import { ApiError, isTerminalError } from "@/api/client";
import { Button, IconButton } from "@/components/ui/Button";
import { Notice } from "@/components/ui/Notice";

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-ink-muted" role="status">
      <span
        aria-hidden="true"
        className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-brand-500"
      />
      {label}
    </div>
  );
}

// What a status says on its own. The API answers a bare status this way when it
// has nothing more specific to add (API Gateway, or ``HTTP <n>`` from the client).
const BARE_STATUS_TEXT =
  /^(http \d{3}|unauthorized|forbidden|not found|internal server error|bad gateway|service unavailable|gateway timeout)\.?$/;

// What the browser says when ``fetch`` itself fails (Chrome "Failed to fetch",
// Firefox "NetworkError when…", Safari "Load failed"): always a TypeError.
const FETCH_FAILURE = /failed to fetch|networkerror|network request failed|load failed/i;

// Maps recurring backend/technical errors to a calmer, business-facing
// sentence. The raw message stays available behind a "Technical details"
// disclosure so nothing is hidden from the operator who needs it.
//
// For an ``ApiError`` the decision rests on the status and the error code,
// never on digits or words found in the message: an identifier, an ARN or a
// tenant slug can contain "500" or "network" without the call having failed
// that way. For anything else only the browser's own fetch-failure wording
// counts, and only on a TypeError — a SyntaxError that quotes the word
// "network" is not a network failure.
function friendlyMessage(error: unknown, raw: string): string | null {
  if (!(error instanceof ApiError)) {
    const network =
      (error instanceof TypeError && FETCH_FAILURE.test(raw)) ||
      raw.trim().toLowerCase() === "network error"; // oidc-client-ts
    return network
      ? "Could not reach the service. Check your network and try again."
      : null;
  }
  const { status } = error;
  if (status === 401) return "Session expired. Sign in again to continue.";
  // A specific server sentence is the reason the operator needs ("Production
  // key rejected" on a 403 is the expected failure of a destructive action):
  // only a bare status gets replaced. The backend's catch-all 500 is the one
  // exception: its text sends the reader to "CloudWatch logs", an
  // infrastructure detail that has no place in the GUI (it stays under
  // "Technical details"), and it is identified by its code, not its wording.
  const catchAll = status >= 500 && error.errorCode === "InternalServerError";
  if (!catchAll && !BARE_STATUS_TEXT.test(raw.trim().toLowerCase())) return null;
  if (status === 403) return "This action isn't allowed with your account.";
  if (status === 404) {
    return "Item not found — it may have been deleted in the meantime.";
  }
  if (status >= 500) {
    return "The service ran into an error. Please try again in a moment.";
  }
  return null;
}

// Said by the error box of a failed action, of a failed load and of a page that crashed (`RouteError`), so
// it must never throw itself: `String()` does on a value with no prototype (`Object.create(null)`), on an
// object whose `toString` throws, and on a `Symbol` inside a template — and an error box that throws
// takes the whole page with it, the error page included.
function errorText(error: unknown): string {
  try {
    return error instanceof Error ? error.message : String(error ?? "Unknown error");
  } catch {
    return "Unknown error";
  }
}

/**
 * Something that failed, said in a calmer sentence than the raw error, with the
 * raw error one click away ("Technical details"). `title` is the bold first
 * line ("Could not load tenants"), and `onRetry` puts a Retry button beside it
 * (`retrying` while the new attempt runs) — for the failure of a LOAD, where trying
 * again is the way forward. A refused ACTION has none: the operator fixes the
 * input and clicks the action again. `onDismiss` puts a close button beside it, for an error
 * that would otherwise stay until the action is run again (see `MutationError`).
 */
export function ErrorBox({
  error,
  title,
  onRetry,
  retrying = false,
  onDismiss,
}: {
  error: unknown;
  title?: string;
  onRetry?: () => void;
  retrying?: boolean;
  onDismiss?: () => void;
}) {
  const raw = errorText(error);
  const friendly = friendlyMessage(error, raw);

  return (
    // ``role="alert"``: an error that appears after a click (a refused action
    // inside a modal, a failed save) is announced without moving focus.
    <Notice
      tone="danger"
      role="alert"
      title={title}
      actions={
        (onRetry || onDismiss) && (
          <div className="flex items-center gap-1">
            {onRetry && (
              <Button variant="ghost" size="sm" loading={retrying} onClick={onRetry}>
                {retrying ? "Retrying…" : "Retry"}
              </Button>
            )}
            {onDismiss && <IconButton icon={X} size="sm" label="Dismiss this error" onClick={onDismiss} />}
          </div>
        )
      }
    >
      <p>{friendly ?? raw}</p>
      {friendly && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs hover:underline">Technical details</summary>
          <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-xs">{raw}</pre>
        </details>
      )}
    </Notice>
  );
}

/**
 * The failure of ONE action, said beside THAT action. A page with several actions gives each its own
 * slot — its own titled box, in view of its own button (or in its own dialog) — because one shared box
 * (or a chain of ``a.error ?? b.error``) shows only the first failure, keeps a stale one on screen over
 * a newer one, and does not say which action failed. The box is a `role="alert"` message that names
 * what failed (`title`), and has a close button that resets the mutation: it is gone when the
 * operator says so, when the action is run again, or when the action is cancelled — never left
 * behind after a success.
 *
 * `view` lets a refusal that has something specific to say (a 409, a 412) draw itself instead of
 * the generic box; return `undefined` for the generic one.
 */
export function MutationError({
  mutation,
  title,
  view,
  className,
}: {
  mutation: { isError: boolean; error: unknown; reset: () => void };
  title: string;
  view?: (error: unknown) => ReactNode | undefined;
  /** Layout only (a margin). */
  className?: string;
}) {
  if (!mutation.isError) return null;
  const special = view?.(mutation.error);
  return (
    <div className={className}>
      {special ?? <ErrorBox error={mutation.error} title={title} onDismiss={mutation.reset} />}
    </div>
  );
}

/**
 * What a list or a table needs to say "this could not be loaded": the error, and
 * a way to try again. Built by `loadFailure(query)` from the page's query, and
 * `undefined` while there is something to show — a failed REFRESH keeps the last
 * data on screen behind a `RefreshFailed` notice instead (see below).
 */
export interface LoadFailure {
  error: unknown;
  onRetry: () => void;
  retrying: boolean;
}

/** The blocking failure of a query (nothing to show), or `undefined`. Works for a plain and an infinite query. */
export function loadFailure(query: {
  isLoadingError: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => unknown;
}): LoadFailure | undefined {
  if (!query.isLoadingError) return undefined;
  return { error: query.error, onRetry: () => void query.refetch(), retrying: query.isFetching };
}

/**
 * "Nothing to show yet, and a fetch is expected." TanStack's ``isLoading`` is
 * false while a fetch is PAUSED (browser offline), so a page guarded by it
 * alone falls through and renders with ``data`` undefined — for an edit page,
 * an empty editable form. A disabled query (``fetchStatus: "idle"``) is not
 * waiting for anything, which is why this is not just ``isPending``.
 */
export function isLoadingOrPaused(query: {
  isPending: boolean;
  fetchStatus: string;
}): boolean {
  return query.isPending && query.fetchStatus !== "idle";
}

interface RefreshableQuery {
  isRefetchError: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => unknown;
  /**
   * An INFINITE query only: its "Load more" failed. TanStack keeps that failure out of
   * `isRefetchError` (it is the next page that failed, not a refresh), so without this flag a
   * list whose second page could not be fetched would say nothing at all.
   */
  isFetchNextPageError?: boolean;
  fetchNextPage?: () => unknown;
}

/**
 * Notice for a query whose background refresh failed while its last good data
 * is still on screen (a poll during a deployment, the refetch after an action).
 * Such a page must NOT be swapped for an ``ErrorBox``: that unmounts its open
 * modals and forms, and a page without polling never recovers by itself. Pair
 * it with ``isLoadingError`` for the blocking case, where there is nothing to
 * show: ``if (q.isLoadingError) return <ErrorBox …/>`` then ``<RefreshFailed
 * query={q} />`` at the top of the page. Renders nothing while the data is fresh.
 *
 * On an infinite list it also speaks for a failed "Load more": the rows already
 * loaded stay, and Retry asks for the SAME next page (not for every page again).
 */
export function RefreshFailed({
  query,
  className = "",
}: {
  query: RefreshableQuery;
  /** Spacing from the page's own layout; only applied while the notice shows. */
  className?: string;
}) {
  const moreFailed = query.isFetchNextPageError === true;
  if (!query.isRefetchError && !moreFailed) return null;
  const retry = moreFailed && query.fetchNextPage ? query.fetchNextPage : query.refetch;
  const raw = errorText(query.error);
  const retryable = !isTerminalError(query.error);
  return (
    <Notice
      tone="warning"
      role="status"
      className={className}
      actions={
        retryable && (
          <Button
            variant="ghost"
            size="sm"
            loading={query.isFetching}
            onClick={() => void retry()}
          >
            {query.isFetching ? "Retrying…" : "Retry"}
          </Button>
        )
      }
    >
      <p>
        {moreFailed
          ? "Couldn't load more — the rows already loaded are still shown."
          : "Couldn't refresh — this page shows the last data received."}
      </p>
      {/* The raw text stays reachable (hover) when a friendly sentence replaced it. */}
      <p className="mt-0.5 break-words text-xs" title={raw}>
        {friendlyMessage(query.error, raw) ?? raw}
      </p>
    </Notice>
  );
}
